import { fetchApi } from "./api"
import {
  normalizeAlfredDevice,
  normalizeAlfredDevicesResponse,
} from "./alfred-normalize"
import { mockAlfredDeviceService } from "@/mocks/mock-service"
import type {
  AlfredDeviceConfig,
  AlfredDeviceListParams,
  AlfredDevicesResponse,
  BulkDeleteAlfredDevicesResult,
  BulkUpdateAlfredDevicesRequest,
  BulkUpdateAlfredDevicesResult,
  CloneAlfredDeviceRequest,
  CreateAlfredDeviceRequest,
  UpdateAlfredDeviceRequest,
} from "@/types/alfred-device"
import { MAX_ALFRED_BULK_DEVICE_UPDATES } from "@/types/alfred-device"
import { ApiException } from "@/types/api"
import type {
  AlfredBulkImportJobStatusResponse,
  AlfredBulkImportRowError,
  AlfredBulkImportUploadResult,
} from "@/types/alfred-bulk-import"

const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === "true"

const BASE = "/api/v1/config/alfred/devices"

interface AlfredBulkImportApiRowError {
  row: number
  deviceId?: string
  clientId?: string
  error: string
}

interface AlfredBulkImportJobStatusApiResponse
  extends Omit<AlfredBulkImportJobStatusResponse, "errorSample" | "skippedDuplicate"> {
  errorSample?: AlfredBulkImportRowError[]
  results?: AlfredBulkImportApiRowError[]
  skippedDuplicate?: number
}

function normalizeBulkImportJobStatus(
  response: AlfredBulkImportJobStatusApiResponse,
): AlfredBulkImportJobStatusResponse {
  const { errorSample, results, skippedDuplicate, ...job } = response

  return {
    ...job,
    skippedDuplicate: skippedDuplicate ?? 0,
    errorSample:
      errorSample ??
      results?.map(({ row, ...result }) => ({ ...result, rowNumber: row })) ??
      [],
  }
}

function isBulkUpdateResult(value: unknown): value is BulkUpdateAlfredDevicesResult {
  if (typeof value !== "object" || value === null) return false

  const result = value as Partial<BulkUpdateAlfredDevicesResult>
  return (
    typeof result.requestedCount === "number" &&
    typeof result.successCount === "number" &&
    typeof result.failureCount === "number" &&
    Array.isArray(result.failedDevices)
  )
}

function toQueryString(params: AlfredDeviceListParams): string {
  const q = new URLSearchParams()
  if (params.page) q.set("page", String(params.page))
  if (params.pageSize) q.set("pageSize", String(params.pageSize))
  if (params.filter) q.set("filter", params.filter)
  if (params.sortBy) q.set("sortBy", params.sortBy)
  if (params.sortOrder) q.set("sortOrder", params.sortOrder)
  const s = q.toString()
  return s ? `?${s}` : ""
}

const realAlfredDeviceService = {
  async getAll(params: AlfredDeviceListParams = {}): Promise<AlfredDevicesResponse> {
    const response = await fetchApi<AlfredDevicesResponse>(`${BASE}${toQueryString(params)}`)
    return normalizeAlfredDevicesResponse(response)
  },

  async getByClient(clientId: number, params: AlfredDeviceListParams = {}): Promise<AlfredDevicesResponse> {
    const response = await fetchApi<AlfredDevicesResponse>(
      `/api/v1/config/alfred/clients/${clientId}/devices${toQueryString(params)}`,
    )
    return normalizeAlfredDevicesResponse(response)
  },

  async getById(id: number): Promise<AlfredDeviceConfig> {
    const device = await fetchApi<AlfredDeviceConfig>(`${BASE}/${id}`)
    return normalizeAlfredDevice(device)
  },

  async create(data: CreateAlfredDeviceRequest): Promise<AlfredDeviceConfig> {
    const device = await fetchApi<AlfredDeviceConfig>(BASE, {
      method: "POST",
      body: JSON.stringify(data),
    })
    return normalizeAlfredDevice(device)
  },

  async clone(sourceDevicePk: number, data: CloneAlfredDeviceRequest): Promise<AlfredDeviceConfig> {
    const device = await fetchApi<AlfredDeviceConfig>(`${BASE}/${sourceDevicePk}/clone`, {
      method: "POST",
      body: JSON.stringify(data),
    })
    return normalizeAlfredDevice(device)
  },

  async bulkCreate(file: File): Promise<AlfredBulkImportUploadResult> {
    const body = new FormData()
    body.append("file", file)

    return fetchApi<AlfredBulkImportUploadResult>(`${BASE}/bulk/create`, {
      method: "POST",
      body,
    })
  },

  async getBulkImportJob(jobId: string): Promise<AlfredBulkImportJobStatusResponse> {
    const response = await fetchApi<AlfredBulkImportJobStatusApiResponse>(
      `${BASE}/bulk/${encodeURIComponent(jobId)}`,
    )
    return normalizeBulkImportJobStatus(response)
  },

  async update(id: number, data: UpdateAlfredDeviceRequest): Promise<AlfredDeviceConfig> {
    const device = await fetchApi<AlfredDeviceConfig>(`${BASE}/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
    return normalizeAlfredDevice(device)
  },

  async bulkUpdate(data: BulkUpdateAlfredDevicesRequest): Promise<BulkUpdateAlfredDevicesResult> {
    if (data.ids.length < 1 || data.ids.length > MAX_ALFRED_BULK_DEVICE_UPDATES) {
      throw new Error(
        `ids must contain between 1 and ${MAX_ALFRED_BULK_DEVICE_UPDATES} entries`,
      )
    }
    try {
      return await fetchApi<BulkUpdateAlfredDevicesResult>(`${BASE}/bulk`, {
        method: "PUT",
        body: JSON.stringify(data),
      })
    } catch (error) {
      // When every device fails, the API returns the structured aggregate result
      // with HTTP 400 or 500. Preserve it so the UI can report per-device failures.
      if (error instanceof ApiException && isBulkUpdateResult(error.payload)) {
        return error.payload
      }
      throw error
    }
  },

  async bulkDelete(ids: number[]): Promise<BulkDeleteAlfredDevicesResult> {
    if (ids.length < 1 || ids.length > MAX_ALFRED_BULK_DEVICE_UPDATES) {
      throw new Error(
        `ids must contain between 1 and ${MAX_ALFRED_BULK_DEVICE_UPDATES} entries`,
      )
    }

    const result = await fetchApi<BulkDeleteAlfredDevicesResult | null>(
      `${BASE}/bulk/delete`,
      {
        method: "POST",
        body: JSON.stringify({ ids }),
      },
    )

    return result ?? {
      results: ids.map((id) => ({ id, status: "deleted" as const })),
      deleted: ids.length,
      failed: 0,
    }
  },

  async delete(id: number): Promise<void> {
    return fetchApi<void>(`${BASE}/${id}`, { method: "DELETE" })
  },
}

export const alfredDeviceService = USE_MOCKS ? mockAlfredDeviceService : realAlfredDeviceService

if (import.meta.env.DEV) {
  console.log(`🔧 Alfred Device Service: ${USE_MOCKS ? "MOCK MODE" : "API MODE"}`)
}
